import { FlowNodeData } from "@tenforce/product-fields"

import { IItemStatus, IStatusTransition } from "../../../models"

// This belongs to the faket status that shows up as `*` in the status transition GDL
// Each item type has its own any status, all with `_Any_` code
// They all use a fixed code which is set in Projects/Robinson/Framework/Mappings/ItemStatus.cs as AnyStatusCode
export const anyStatusCode = "_Any_"

// TRANSFORMATION FROM STATUS ID TO FLOW NODE
const getFlowNodeByStatus = (
    status: IItemStatus,
    current: IItemStatus,
): FlowNodeData => {
    return {
        id: status.id,
        label: status.label,
        isCurrent: current.id === status.id,
    }
}

export const getFlowNodesByStatuses = (
    statuses: IItemStatus[],
    current: IItemStatus,
): FlowNodeData[] => {
    return statuses.map((status) => getFlowNodeByStatus(status, current))
}

// FLOW CALCULATION
const collectFilteredTransitions = (
    currentStatus: IItemStatus,
    transitions: IStatusTransition[],
    matches: (transition: IStatusTransition, status: IItemStatus) => boolean,
    getNextStatus: (transition: IStatusTransition) => IItemStatus,
): IStatusTransition[] => {
    const foundTransitions = transitions.filter((transition) =>
        matches(transition, currentStatus),
    )

    // then get the ones that are with the lowest sequence numbers
    const minSequenceNr = Math.min(
        ...foundTransitions.map((transition) => transition.sequenceNr),
    )

    const filteredTransitions = foundTransitions.filter(
        (transition) => transition.sequenceNr === minSequenceNr,
    )

    // Multiple transitions from the same status to a target status (explicitly or via an '*'),
    // are collapsed and treated as one. Example:
    // Not Started -> Ongoing, SQ:0, type: primary
    // Not Started -> Ongoing, SQ:0, type: secondary
    // Not Started -> *, SQ:0, type: hidden => these are replaced with every possible status
    const nextStatusIds = filteredTransitions.map(
        (transition) => getNextStatus(transition).id,
    )

    // so if the next statuses are always the same, we need to care only about one of them
    if (filteredTransitions.length > 0 && new Set(nextStatusIds).size === 1) {
        return [filteredTransitions[0]]
    }

    return filteredTransitions
}

const collectStatuses = (
    currentStatus: IItemStatus,
    transitions: IStatusTransition[],
    getNextStatus: (transition: IStatusTransition) => IItemStatus,
    matches: (transition: IStatusTransition, status: IItemStatus) => boolean,
): IItemStatus[][] => {
    const result: IItemStatus[][] = []

    // look for the transitions that matches the current search criteria
    let foundTransitions = collectFilteredTransitions(
        currentStatus,
        transitions,
        matches,
        getNextStatus,
    )

    // track visited IDs to prevent infinite loops
    const visited = new Set<number>([currentStatus.id])

    // search goes until we can ensure,
    // that there is only one incoming/outgoing transition
    while (foundTransitions.length === 1) {
        // we know there is only 1 id here
        const nextStatus = getNextStatus(foundTransitions[0])

        // to prevent infinite loops
        if (visited.has(nextStatus.id)) {
            // we still add it, so we can see it's there twice
            // and know when to fade out
            result.push([nextStatus])
            break
        }

        visited.add(nextStatus.id)
        result.push([nextStatus])

        // then we keep filtering for a new transition incoming/outgoing to/from this one
        currentStatus = nextStatus

        foundTransitions = collectFilteredTransitions(
            currentStatus,
            transitions,
            matches,
            getNextStatus,
        )
    }

    // if we found more than 1
    // then we save the next statuses and stop the search
    if (foundTransitions.length > 1) {
        result.push(foundTransitions.map(getNextStatus))
    }

    // in case of 0, we also stop the search
    return result
}

export const getFollowingStatuses = (
    current: IItemStatus,
    transitions: IStatusTransition[],
): IItemStatus[][] =>
    collectStatuses(
        current,
        transitions,
        // we are looking for the statuses,
        // that are reachable from the current one
        (transition) => transition.toStatus,
        (transition, status) =>
            // ensure we don't end up in the same status
            transition.toStatus.id !== status.id &&
            transition.fromStatus.id === status.id,
    )

export const getPreviousStatuses = (
    current: IItemStatus,
    transitions: IStatusTransition[],
): IItemStatus[][] =>
    collectStatuses(
        current,
        transitions,
        // we are looking for the statuses,
        // that go to the current one
        (transition) => transition.fromStatus,
        (transition, status) =>
            // ensure we don't end up in the same status
            transition.fromStatus.id !== status.id &&
            transition.toStatus.id === status.id,
    )
